import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  TextField,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Psychology as AIIcon,
  Code as CodeIcon,
  Create as WriteIcon,
  Science as ResearchIcon,
  Assistant as AssistantIcon,
} from '@mui/icons-material';
import FullscreenButton from './FullscreenButton';

function AgentGallery() {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [selectedTab, setSelectedTab] = useState(0);
  const [previewAgent, setPreviewAgent] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Mock agent data
  useEffect(() => {
    const mockAgents = [
      {
        id: '1',
        name: 'Research Assistant Pro',
        description: 'Advanced AI agent for comprehensive research tasks, data analysis, and academic writing support.',
        category: 'research',
        author: 'AI Labs',
        rating: 4.8,
        downloads: 1250,
        tags: ['research', 'data-analysis', 'academic'],
        type: 'public',
        featured: true,
        version: '2.1.0',
        lastUpdated: new Date(Date.now() - 86400000 * 2),
        capabilities: ['Web Research', 'Data Analysis', 'Report Generation', 'Citation Management'],
        price: 'Free',
        screenshots: [],
      },
      {
        id: '2',
        name: 'Code Reviewer',
        description: 'Intelligent code review agent that analyzes code quality, suggests improvements, and finds bugs.',
        category: 'coding',
        author: 'DevTools Inc',
        rating: 4.6,
        downloads: 980,
        tags: ['code-review', 'debugging', 'quality-assurance'],
        type: 'premium',
        featured: false,
        version: '1.5.2',
        lastUpdated: new Date(Date.now() - 86400000 * 5),
        capabilities: ['Code Analysis', 'Bug Detection', 'Performance Tips', 'Security Audit'],
        price: '$9.99/month',
        screenshots: [],
      },
      {
        id: '3',
        name: 'Content Creator',
        description: 'Creative writing assistant for blogs, articles, social media posts, and marketing content.',
        category: 'writing',
        author: 'Creative AI',
        rating: 4.4,
        downloads: 2100,
        tags: ['content-creation', 'copywriting', 'social-media'],
        type: 'public',
        featured: true,
        version: '3.0.1',
        lastUpdated: new Date(Date.now() - 86400000 * 1),
        capabilities: ['Blog Writing', 'Social Media Posts', 'Ad Copy', 'SEO Optimization'],
        price: 'Free',
        screenshots: [],
      },
      {
        id: '4',
        name: 'Data Scientist Helper',
        description: 'Specialized agent for data science workflows, statistical analysis, and machine learning projects.',
        category: 'data-science',
        author: 'DataPro',
        rating: 4.9,
        downloads: 750,
        tags: ['data-science', 'machine-learning', 'statistics'],
        type: 'premium',
        featured: false,
        version: '1.8.0',
        lastUpdated: new Date(Date.now() - 86400000 * 3),
        capabilities: ['Statistical Analysis', 'ML Model Training', 'Data Visualization', 'Predictive Analytics'],
        price: '$19.99/month',
        screenshots: [],
      },
      {
        id: '5',
        name: 'Personal Assistant',
        description: 'General-purpose AI assistant for daily tasks, scheduling, reminders, and productivity.',
        category: 'assistant',
        author: 'AI Community',
        rating: 4.2,
        downloads: 3200,
        tags: ['productivity', 'scheduling', 'general-purpose'],
        type: 'public',
        featured: false,
        version: '2.3.1',
        lastUpdated: new Date(Date.now() - 86400000 * 7),
        capabilities: ['Task Management', 'Calendar Integration', 'Email Drafting', 'Quick Answers'],
        price: 'Free',
        screenshots: [],
      },
    ];

    setAgents(mockAgents);
    setFilteredAgents(mockAgents);
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = agents;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((agent) => agent.category === selectedCategory);
    }

    // Filter by tab (featured, free, premium)
    if (selectedTab === 1) { // Featured
      filtered = filtered.filter((agent) => agent.featured);
    } else if (selectedTab === 2) { // Free
      filtered = filtered.filter((agent) => agent.price === 'Free');
    } else if (selectedTab === 3) { // Premium
      filtered = filtered.filter((agent) => agent.price !== 'Free');
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((agent) => agent.name.toLowerCase().includes(searchTerm.toLowerCase())
        || agent.description.toLowerCase().includes(searchTerm.toLowerCase())
        || agent.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.downloads - a.downloads;
        case 'rating':
          return b.rating - a.rating;
        case 'recent':
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredAgents(filtered);
  }, [agents, selectedCategory, selectedTab, searchTerm, sortBy]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'research': return <ResearchIcon />;
      case 'coding': return <CodeIcon />;
      case 'writing': return <WriteIcon />;
      case 'data-science': return <AIIcon />;
      case 'assistant': return <AssistantIcon />;
      default: return <AIIcon />;
    }
  };

  const handlePreview = (agent) => {
    setPreviewAgent(agent);
    setPreviewOpen(true);
  };

  const handleInstall = (agent) => {
    // Mock installation
    console.log('Installing agent:', agent.name);
    // In real app, this would integrate with agent installation system
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" component="h1">
          Agent Gallery
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/agents/create')}
          >
            Create New Agent
          </Button>
        </Stack>
      </Box>

      {/* Search and Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="research">Research</MenuItem>
                <MenuItem value="coding">Coding</MenuItem>
                <MenuItem value="writing">Writing</MenuItem>
                <MenuItem value="data-science">Data Science</MenuItem>
                <MenuItem value="assistant">Assistant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort by"
              >
                <MenuItem value="popular">Most Popular</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="recent">Recently Updated</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => { /* Navigate to create agent */ }}
            >
              Create Agent
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Category Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="All Agents" />
          <Tab label="Featured" />
          <Tab label="Free" />
          <Tab label="Premium" />
        </Tabs>
      </Box>

      {/* Agent Grid */}
      <Grid container spacing={3}>
        {filteredAgents.map((agent) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            >
              {agent.featured && (
                <Chip
                  label="Featured"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getCategoryIcon(agent.category)}
                  <Typography variant="h6" component="h3" sx={{ ml: 1, flexGrow: 1 }}>
                    {agent.name}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                  {agent.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating value={agent.rating} precision={0.1} size="small" readOnly />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {agent.rating}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  By
                  {' '}
                  {agent.author}
                  {' '}
                  •
                  {' '}
                  {agent.downloads}
                  {' '}
                  downloads
                </Typography>

                <Box sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2,
                }}
                >
                  {agent.tags.slice(0, 3).map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {agent.price}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    v
                    {agent.version}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions sx={{ pt: 0 }}>
                <Button
                  size="small"
                  startIcon={<PreviewIcon />}
                  onClick={() => handlePreview(agent)}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleInstall(agent)}
                >
                  Install
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No results */}
      {filteredAgents.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No agents found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or browse different categories
          </Typography>
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {previewAgent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getCategoryIcon(previewAgent.category)}
                <Box>
                  <Typography variant="h6">{previewAgent.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    by
                    {' '}
                    {previewAgent.author}
                    {' '}
                    • v
                    {previewAgent.version}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography variant="body1">{previewAgent.description}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>Capabilities</Typography>
                  <Grid container spacing={1}>
                    {previewAgent.capabilities.map((capability, index) => (
                      <Grid item key={index}>
                        <Chip label={capability} variant="outlined" />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Rating</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rating value={previewAgent.rating} precision={0.1} size="small" readOnly />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {previewAgent.rating}
                          /5.0
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Downloads</Typography>
                      <Typography variant="body1">{previewAgent.downloads.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Price</Typography>
                      <Typography variant="body1" color="primary" fontWeight="bold">
                        {previewAgent.price}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body1">
                        {previewAgent.lastUpdated.toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>Tags</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {previewAgent.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewOpen(false)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleInstall(previewAgent);
                  setPreviewOpen(false);
                }}
              >
                Install Agent
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default AgentGallery;
